import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import MomentUtils from "@date-io/moment";
import HijriUtils from "@date-io/hijri";
import moment from "moment";
import { timeConversion } from "client-app-core";
import { TextField } from "@mui/material";
import DateTimePickerActionBar from "./CustomComponents/DateTimePicker/DateTimePickerActionBar";
import { useSelector } from "react-redux";
import { getTranslation } from "orion-components/i18n";
import { styled } from "@mui/material/styles";

const CustomizedTextField = styled(TextField)(({ customStyles = {} }) => ({
	"& label": {
		color: "#9A9FA3",
		fontSize: 12,
		...customStyles.label
	},
	"& label.Mui-focused": {
		color: "#9A9FA3",
		...customStyles.labelFocused
	},
	"& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall": {
		padding: "0px !important"
	},
	"& .MuiList-root MuiList-padding MuiMenu-list": {
		background: "black"
	},
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571",
			width: 1,
			...customStyles.fieldset
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetHover
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetFocused
		},
		// date picker input field
		"&.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-formControl.MuiInputBase-sizeSmall.Mui-readOnly":
		{
			"& .MuiInputBase-input": {
				fontSize: 10,
				color: "#FFFFFF"
			}
		},
		...customStyles.inputRoot
	}
}));

const darkThemeSx = {
	"& .MuiPickersDay-dayWithMargin": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},
	"& .MuiCalendarPicker-root": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},
	"& .MuiTabs-flexContainer": {
		backgroundColor: "#2C2C2E"
	},
	"& .MuiTabs-indicator": {
		border: "2px solid #88134A",
		backgroundColor: "#88134A"
	}
};

const propTypes = {
	id: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	value: PropTypes.object,
	handleChange: PropTypes.func.isRequired,
	format: PropTypes.string,
	minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	disabled: PropTypes.bool,
	margin: PropTypes.string,
	dir: PropTypes.string,
	okLabel: PropTypes.bool,
	cancelLabel: PropTypes.bool,
	clearLabel: PropTypes.bool,
	locale: PropTypes.string,
	theme: PropTypes.string,
	options: PropTypes.object,
	customDialogProps: PropTypes.object,
	useInputControl: PropTypes.bool,
	showDialog: PropTypes.bool,
	onSubmit: PropTypes.func,
	onExternalReset: PropTypes.func
};

const defaultProps = {
	id: "time-picker",
	label: "",
	format: "time_12-hour",
	value: null,
	minDate: "1900-01-01",
	disabled: false,
	margin: "normal",
	locale: "en",
	theme: "dark",
	clearLabel: true,
	okLabel: true,
	cancelLabel: true,
	options: {},
	customDialogProps: {},
	displaySeconds: false,
	useCustomTextField: false,
	useInputControl: true,
	showDialog: false,
	onSubmit: () => { },
	onExternalReset: () => { }
};

const CBTimePicker = ({
	id,
	label,
	value,
	handleChange,
	format,
	minDate,
	disabled,
	margin,
	dir,
	okLabel,
	cancelLabel,
	clearLabel,
	theme,
	options,
	customDialogProps,
	useCustomTextField,
	displaySeconds,
	useInputControl,
	showDialog,
	onSubmit
}) => {
	const [isPickerOpen, setPickerOpen] = useState(showDialog); // State to control picker visibility
	const [lastKnownValue, setLastKnownValue] = useState(value); // Stores last known value

	const handleCancel = () => {
		handleChange(lastKnownValue); // Revert to last known value
		onSubmit(); // closes the time picker
	};

	useEffect(() => {
		if (!useInputControl) {
			setPickerOpen(showDialog); // Automatically open when showDialog is true
		}
	}, [showDialog, useInputControl]);

	const locale = useSelector((state) => state.i18n && state.i18n.locale);
	const localeOverride = locale === "ar_kw" ? "en" : locale;

	const styles = {
		inputLabelProps: {
			...(dir === "ltr" && { transformOrigin: "top left" }),
			...(dir === "rtl" && {
				transformOrigin: "top right",
				left: "unset",
				textAlign: "right"
			})
		}
	};

	const formatTime = (date, invalidLabel, format) => {
		return date === null
			? ""
			: format && date
				? timeConversion.convertToUserTime(date, format, localeOverride, displaySeconds)
				: invalidLabel;
	};

	moment.locale(localeOverride);
	const twelveHourFormat = !format.includes("24-hour") || format.toLowerCase().includes("hh:mm a");

	const actions = [
		clearLabel ? getTranslation("global.CBComponents.DateTime.clear") : null,
		cancelLabel ? getTranslation("global.CBComponents.DateTime.cancel") : null,
		okLabel ? getTranslation("global.CBComponents.DateTime.ok") : null
	];

	const setActualTime = () => {
		handleChange(value);
		setLastKnownValue(value);
		onSubmit();
	};

	const clearTime = () => {
		handleChange(null);
		onSubmit(null);
	};

	let error;
	if (options?.error) {
		error = options.error;
	} else if (value === null) {
		error = false;
	} else {
		error = null;
	}

	return (
		<LocalizationProvider
			dateAdapter={locale === "ar" ? HijriUtils : MomentUtils}
			localeText={localeOverride}
			adapterLocale={localeOverride}
		>
			<MobileTimePicker
				{...(!useInputControl ? { open: isPickerOpen } : {})}
				id={id}
				label={label}
				value={moment(value)}
				onChange={handleChange}
				showToolbar={true}
				closeOnSelect={false}
				minDate={moment(minDate)}
				disabled={disabled}
				ampm={twelveHourFormat} // cSpell:ignore ampm
				{...(displaySeconds ? { views: ["hours", "minutes", "seconds"] } : {})}
				renderInput={(params) => {
					if (!useInputControl) {
						return <></>;
					} else {
						params.inputProps.value = formatTime(value, "Invalid Time", format);
						return useCustomTextField ? (
							<CustomizedTextField
								variant="outlined"
								{...params}
								{...options}
								size="small"
								margin={margin}
								error={error}
								InputLabelProps={{
									style: styles.inputLabelProps
								}}
							/>
						) : (
							<TextField
								{...params}
								{...options}
								fullWidth
								margin={margin}
								error={error}
								variant="standard"
								InputLabelProps={{
									style: styles.inputLabelProps
								}} data-testid="orion-components-cbcomponents-date-time-cbtime-picker-text-field"
							/>
						);
					}
				}}
				DialogProps={{
					sx: theme === "dark" ? darkThemeSx : {},
					style: { direction: "ltr", ...customDialogProps },
					className: theme === "dark" ? "cbDateTimePicker" : "lightcbDateTimePicker"
				}}
				toolbarTitle={<div>&nbsp;</div>}
				toolbarPlaceholder={null}
				componentsProps={{
					actionBar: {
						actions,
						resetDateTime: handleCancel,
						submit: setActualTime,
						clearTime: clearTime
					}
				}}
				components={{
					ActionBar: DateTimePickerActionBar
				}} data-testid="orion-components-cbcomponents-date-time-cbtime-picker-mobile-time-picker"
			/>
		</LocalizationProvider>
	);
};

CBTimePicker.propTypes = propTypes;
CBTimePicker.defaultProps = defaultProps;

export default CBTimePicker;
